import axios from '@/config/axios';
import { useToast } from 'vue-toastification';
import * as bootstrap from 'bootstrap';
import { mapGetters, mapActions } from 'vuex';
import { clearTrendingCache } from '@/utils/trendingCache';
import { clearRecommendCache } from '@/utils/recommendCache';
import NotificationBell from '@/components/notification-bell/NotificationBell.vue';
import OldBookCartDrawer from '@/components/cart/OldBookCartDrawer.vue';

export default {
  name: 'Header',
  components: {
    NotificationBell,
    OldBookCartDrawer
  },
  data() {
    return {
      displayName: 'Thành viên',
      userAvatar: '',
      isScrolled: false,
      headerSearchKey: '',
      isLoggedIn: false,
      showSuggestions: false,
      isSuggestLoading: false,
      recentSearches: [],
      suggestedBooks: []
    };
  },
  watch: {
    '$route': {
      handler(toRoute) {
        this.closeDropdowns();
        if (toRoute && toRoute.path === '/search') {
          const key = (history.state && history.state.key !== undefined)
            ? history.state.key
            : (toRoute.query?.key || sessionStorage.getItem('lastSearchKey') || window.currentSearchKey || '');
          this.headerSearchKey = key;
          window.currentSearchKey = key;
        }
      },
      immediate: true,
      deep: true
    },
    headerSearchKey(newVal) {
      this.getSuggestions(newVal);
      if (window.currentSearchKey !== newVal) {
        window.currentSearchKey = newVal;
        const event = new CustomEvent('search-key-sync', { detail: newVal });
        window.dispatchEvent(event);
      }
    }
  },
  computed: {
    ...mapGetters('cart', [
      'totalItems'
    ]),
    pageTitle() {
      return this.$route.meta?.title || 'Lối Đọc';
    },
    filteredRecentSearches() {
      if (!Array.isArray(this.recentSearches)) return [];
      if (!this.headerSearchKey) return this.recentSearches;
      const query = (this.headerSearchKey || '').toLowerCase();
      return this.recentSearches.filter(item =>
        typeof item === 'string' && item.toLowerCase().includes(query)
      );
    },
    filteredBooks() {
      return this.suggestedBooks;
    }
  },
  mounted() {
    this.loadUserInfo();
    window.addEventListener('scroll', this.handleScroll);
    document.addEventListener('mousedown', this.handleDocumentClick);
    window.addEventListener('search-key-sync', this.handleSearchKeySync);
    window.addEventListener('avatar-updated', this.handleAvatarUpdated);
    this.loadRecentSearches();
    document.body.classList.add('has-ipad-rail');
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    document.removeEventListener('mousedown', this.handleDocumentClick);
    window.removeEventListener('search-key-sync', this.handleSearchKeySync);
    window.removeEventListener('avatar-updated', this.handleAvatarUpdated);
    document.body.classList.remove('has-ipad-rail');
  },
  methods: {
    ...mapActions('cart', [
      'openDrawer',
      'toggleDrawer'
    ]),
    handleAvatarUpdated(e) {
      this.userAvatar = (e && e.detail && e.detail !== 'NO_AVATAR') ? e.detail : '';
    },
    loadUserInfo() {
      const token = localStorage.getItem('token');
      this.isLoggedIn = token !== null;
      const storedAvatar = localStorage.getItem('userAvatar');
      this.userAvatar = storedAvatar && storedAvatar !== 'NO_AVATAR' ? storedAvatar : '';

      const currentAccountStr = localStorage.getItem('current-account');
      if (currentAccountStr && this.isLoggedIn) {
        try {
          const currentAccount = JSON.parse(currentAccountStr);
          let uName = currentAccount.username;
          if (uName && uName.startsWith('[DRAFF]')) uName = null;
          this.displayName = currentAccount.name || currentAccount.fullName || currentAccount.fullname || uName || 'Thành viên';
        } catch (e) {
          console.error('Error parsing current-account:', e);
        }
      }
    },
    handleScroll() {
      this.isScrolled = window.scrollY > 180;
    },
    focusSearch(type) {
      try {
        const input = type === 'mobile' ? this.$refs.mobileSearchInput : this.$refs.desktopSearchInput;
        if (input && typeof input.focus === 'function') {
          input.focus();
        }
        this.openSuggestions();
      } catch (e) {
        console.warn('focusSearch error:', e);
      }
    },
    openSuggestions() {
      try {
        this.loadRecentSearches();
        this.showSuggestions = true;
      } catch (e) {
        console.warn('openSuggestions error:', e);
      }
    },
    clearBookcaseState() {
      try {
        sessionStorage.removeItem('bookcase_state');
      } catch (e) {
        // silent
      }
    },
    loadRecentSearches() {
      try {
        const stored = localStorage.getItem('recentSearches');
        const parsed = stored ? JSON.parse(stored) : [];
        this.recentSearches = Array.isArray(parsed)
          ? parsed.filter(item => typeof item === 'string' && item.trim().length > 0)
          : [];
      } catch (e) {
        console.error('Error loading recent searches:', e);
        this.recentSearches = [];
      }
    },
    saveSearchKey(key) {
      try {
        if (!key || typeof key !== 'string' || !key.trim()) return;
        const cleanKey = key.trim();
        const current = Array.isArray(this.recentSearches) ? this.recentSearches : [];
        const filtered = current.filter(item => typeof item === 'string' && item.toLowerCase() !== cleanKey.toLowerCase());
        filtered.unshift(cleanKey);
        this.recentSearches = filtered.slice(0, 5);
        localStorage.setItem('recentSearches', JSON.stringify(this.recentSearches));
      } catch (e) {
        console.error('Error saving recent search:', e);
      }
    },
    removeRecentSearch(val) {
      try {
        const current = Array.isArray(this.recentSearches) ? this.recentSearches : [];
        this.recentSearches = current.filter(item => item !== val);
        localStorage.setItem('recentSearches', JSON.stringify(this.recentSearches));
      } catch (e) {
        console.error('Error removing recent search:', e);
      }
    },
    clearAllRecent() {
      try {
        this.recentSearches = [];
        localStorage.removeItem('recentSearches');
      } catch (e) {
        console.error('Error clearing recent searches:', e);
      }
    },
    selectHeaderSuggestion(val) {
      try {
        this.headerSearchKey = val;
        this.handleHeaderSearch();
      } catch (e) {
        console.error('selectHeaderSuggestion error:', e);
      }
    },
    selectBookSuggestion(book) {
      try {
        if (!book) return;
        if (book.name) {
          this.saveSearchKey(book.name);
        }
        this.showSuggestions = false;
        if (this.$router && book.id) {
          this.$router.push(`/book/${book.id}`).catch(err => {
            if (err && err.name !== 'NavigationDuplicated') {
              console.error(err);
            }
          });
        }
      } catch (e) {
        console.error('selectBookSuggestion error:', e);
      }
    },
    handleCoverError(book) {
      if (book) {
        book.cover = null;
      }
    },
    getAuthorsString(book) {
      if (!book?.authors || book.authors.length === 0) return 'Khuyết danh';
      return book.authors.map(a => a.name).join(', ');
    },
    getTranslatorsString(book) {
      if (!book?.translators || book.translators.length === 0) return '';
      return book.translators.map(t => t.name).join(', ');
    },
    getCategoriesString(book) {
      if (!book?.categories || book.categories.length === 0) return '';
      return book.categories.map(c => c.name).join(', ');
    },
    getSuggestions(query) {
      if (!query || !query.trim()) {
        this.suggestedBooks = [];
        this.isSuggestLoading = false;
        return;
      }
      this.isSuggestLoading = true;
      if (this.suggestTimer) clearTimeout(this.suggestTimer);
      this.suggestTimer = setTimeout(() => {
        axios.get('book/v1/search', {
          params: {
            key: query.trim(),
            page: 0,
            size: 3
          }
        })
          .then(response => {
            const data = response.data;
            if (data.status === '200' && data.result) {
              const content = data.result.content || [];
              this.suggestedBooks = content.map(book => ({
                ...book,
                cover: book.cover && !(book.cover.startsWith('data:') || book.cover.startsWith('http'))
                  ? 'data:image/png;base64,' + book.cover
                  : book.cover
              }));
            } else {
              this.suggestedBooks = [];
            }
          })
          .catch(() => {
            this.suggestedBooks = [];
          })
          .finally(() => {
            this.isSuggestLoading = false;
          });
      }, 250);
    },
    handleSearchKeySync(e) {
      const val = e.detail;
      if (this.headerSearchKey !== val) {
        this.headerSearchKey = val;
      }
    },
    handleDocumentClick(e) {
      if (!this.showSuggestions) return;
      try {
        const el = this.$el;
        if (!el || typeof el.querySelector !== 'function') {
          this.showSuggestions = false;
          return;
        }

        const wrapperDesktop = el.querySelector('.header-search-wrapper');
        const wrapperMobile = el.querySelector('.mobile-header-search');
        
        const path = (e && typeof e.composedPath === 'function') ? e.composedPath() : [];
        
        let isInside = false;
        if (path.length > 0) {
          isInside = (wrapperDesktop && path.includes(wrapperDesktop)) || 
                     (wrapperMobile && path.includes(wrapperMobile));
        } else if (e && e.target && typeof e.target.closest === 'function') {
          isInside = e.target.closest('.header-search-wrapper') || e.target.closest('.mobile-header-search');
        }

        if (!isInside) {
          this.showSuggestions = false;
        }
      } catch (err) {
        this.showSuggestions = false;
      }
    },
    handleHeaderSearch() {
      try {
        const key = (this.headerSearchKey || '').trim();
        if (key) {
          this.saveSearchKey(key);
        }
        window.currentSearchKey = key;
        sessionStorage.setItem('lastSearchKey', key);
        sessionStorage.setItem('lastSearchPage', '0');
        const event = new CustomEvent('search-key-sync', { detail: key });
        window.dispatchEvent(event);
        if (this.$route && this.$route.path === '/search') {
          if (window.history && window.history.replaceState) {
            window.history.replaceState({ ...window.history.state, key, page: 0 }, '');
          }
          const routeEvent = new CustomEvent('search-triggered', { detail: { key, page: 0 } });
          window.dispatchEvent(routeEvent);
        } else if (this.$router) {
          this.$router.push({
            path: '/search',
            state: { key, page: 0 }
          }).catch(err => {
            if (err && err.name !== 'NavigationDuplicated') {
              console.error(err);
            }
          });
        }
        this.showSuggestions = false;
      } catch (e) {
        console.error('handleHeaderSearch error:', e);
      }
    },
    clearHeaderSearch() {
      try {
        this.headerSearchKey = '';
        window.currentSearchKey = '';
        sessionStorage.removeItem('lastSearchKey');
        sessionStorage.removeItem('lastSearchPage');
        const event = new CustomEvent('search-key-sync', { detail: '' });
        window.dispatchEvent(event);
        if (this.$route && this.$route.path === '/search' && this.$router) {
          this.$router.push('/').catch(() => {});
        }
        this.openSuggestions();
      } catch (e) {
        console.error('clearHeaderSearch error:', e);
      }
    },
    closeDropdowns() {
      try {
        ['userDropdown', 'mobileUserDropdown'].forEach(refKey => {
          if (this.$refs && this.$refs[refKey] && bootstrap && bootstrap.Dropdown) {
            const instance = bootstrap.Dropdown.getInstance(this.$refs[refKey]);
            if (instance && typeof instance.hide === 'function') {
              instance.hide();
            }
          }
        });
      } catch (err) {
        // silent
      }
    },
    async handleLogout() {
      this.closeDropdowns();
      try {
        await axios.get('account/logout');
      } catch (err) {
        console.error('Logout API failed:', err);
      }
      try {
        await clearTrendingCache();
        await clearRecommendCache();
      } catch (e) {
        console.error('Clear cache error on logout:', e);
      }
      try {
        window.dispatchEvent(new CustomEvent('user-logged-out'));
      } catch (e) {
        console.error('Event dispatch error:', e);
      }
      const toast = useToast();
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('current-account');
      localStorage.removeItem('userAvatar');
      axios.defaults.headers.common['Authorization'] = '';
      this.isLoggedIn = false;
      this.userAvatar = '';
      toast.success('Đăng xuất thành công!');
      if (this.$router) {
        this.$router.push('/login').catch(err => {
          if (err && err.name !== 'NavigationDuplicated') console.error(err);
        });
      }
    },
    toggleUserDropdown(e) {
      try {
        if (e && typeof e.stopPropagation === 'function') {
          e.stopPropagation();
        }
        const dropdownEl = (e && e.currentTarget) || (this.$refs && (this.$refs.userDropdown || this.$refs.mobileUserDropdown));
        if (dropdownEl && bootstrap && bootstrap.Dropdown) {
          const instance = bootstrap.Dropdown.getOrCreateInstance(dropdownEl);
          if (instance && typeof instance.toggle === 'function') {
            instance.toggle();
          }
        }
      } catch (err) {
        console.warn('Dropdown toggle handled gracefully:', err);
      }
    }
  }
};
